import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { DownloadSimple, X, DeviceMobile, Share } from "@phosphor-icons/react";

const DISMISS_KEY = "necos_pwa_portal_card_dismissed_at";
const COOLDOWN_DAYS = 14;

function wasDismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ageDays = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
    return ageDays < COOLDOWN_DAYS;
  } catch { return false; }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator?.standalone === true;
}

function isMobileUA() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
}

/**
 * Dedicated install card shown on the operative portal. Unlike the global
 * `PWAInstallPrompt` toast (which waits 2 minutes of active use), this card
 * is visible immediately on the portal so operatives — who use the app
 * on their phones all day — are nudged to install right when they land.
 */
export default function PWAInstallCard() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(wasDismissedRecently());
  const [isIOS, setIsIOS] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const iosLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iosLike && !isStandalone());
    setMobile(isMobileUA());
  }, []);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    } else {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setDismissed(true);
    }
  }, [deferredPrompt]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  // Hide on desktop (they probably don't want a mobile-only home-screen app),
  // when already installed, when dismissed within the cooldown, and when we
  // have no way to install (non-iOS without the beforeinstallprompt event).
  if (installed || dismissed || !mobile) return null;
  if (!isIOS && !deferredPrompt) return null;

  return (
    <div className="border border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-4 relative" data-testid="pwa-install-card">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded hover:bg-white/40 dark:hover:bg-white/10 text-muted-foreground"
        aria-label="Dismiss"
        data-testid="pwa-install-card-dismiss"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center shrink-0">
          <DeviceMobile size={20} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {isIOS ? "Add this app to your Home Screen" : "Install this app on your phone"}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
            {isIOS
              ? "Opens instantly, gives you offline support, and sends you push alerts. Takes three taps."
              : "Opens instantly, gives you offline support, and sends you push alerts for new jobs."}
          </p>

          {isIOS ? (
            <ol className="mt-3 space-y-1.5 text-[12px] text-slate-700 dark:text-slate-300">
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-semibold">1</span>
                <span>Tap the <Share size={14} className="inline-block mx-0.5 -mt-0.5 text-primary" /> <strong>Share</strong> button in Safari.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-semibold">2</span>
                <span>Scroll and choose <strong>&quot;Add to Home Screen&quot;</strong>.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-semibold">3</span>
                <span>Tap <strong>Add</strong> in the top right.</span>
              </li>
            </ol>
          ) : (
            <div className="mt-3">
              <Button size="sm" onClick={handleInstall} data-testid="pwa-install-card-install-btn">
                <DownloadSimple size={14} className="mr-1" weight="bold" /> Install now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
